import { ThemeProvider } from "styled-components";
import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { lightTheme } from './utils/Themes.js'; 
import Navbar from "./components/Navbar";
import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from "react-helmet";
import HeroSection from "./components/HeroSection";
import About from "./components/About";
import ProjectsOverview from "./components/ProjectsOverview/index.js";
import Projects from "./components/Projects";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import ServiceList from "./components/ServiceList";
import ProjectDetails from "./components/ProjectDetails";
import styled from "styled-components";
import RoleServiceDetail from './components/RoleServiceDetail/index.js';
import ServiceDetail from "./components/ServiceDetail/index.js";
import CoursePage from "./pages/CoursePage/index.jsx";
import Terms from "./components/Terms/index.js";
import Privacy from "./components/PrivacyPolicy/index.js";
import Refund from "./components/Refund/index.js";
import Testimonials from "./components/Testimonial/index.js";
import TrainingSchedule from "./components/Training Schedule/index.js";
import EventsWebinars from "./components/EventsWebinars/index.js";
import {
  AskQuestionPage,
  ForumPage,
  QuestionDetailPage,
} from "./components/Forum";
import { courses, experiences, getServiceSlug } from "./data/constants.js";

const Body = styled.div`
  background-color: ${({ theme }) => theme.bg};
  width: 100%;
  overflow-x: hidden;
`;

const Wrapper = styled.div`
  background: linear-gradient(38.73deg, rgba(204, 0, 187, 0.15) 0%, rgba(201, 32, 184, 0) 50%), 
              linear-gradient(141.27deg, rgba(0, 70, 209, 0) 50%, rgba(0, 70, 209, 0.15) 100%);
  width: 100%;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 30% 98%, 0 100%);
`;

// require('dotenv').config();

const SITE_URL = "https://magsengineeringlimited.com";
const SITE_NAME = "Mags Engineering Limited";
const DEFAULT_IMAGE = `${SITE_URL}/favicon.jpg`;
const DEFAULT_DESCRIPTION =
  "Mags Engineering Limited delivers engineering training and EPC solutions across process, mechanical, petroleum, and industrial operations.";

const summarizeDescription = (value, fallback = DEFAULT_DESCRIPTION) => {
  const source = Array.isArray(value)
    ? value.find((item) => typeof item === "string" && item.trim())
    : value;
  if (!source || typeof source !== "string") return fallback;
  const compact = source.replace(/\s+/g, " ").trim();
  if (compact.length <= 158) return compact;
  return `${compact.slice(0, 155).trim()}...`;
};

const getCanonicalPath = (pathname) => {
  if (!pathname || pathname === "/") return "/";

  const normalized = pathname.replace(/\/+$/, "") || "/";

  if (/^\/t&c$/i.test(normalized)) {
    return "/T&C";
  }

  return normalized;
};

const getAbsoluteUrl = (value, fallback = DEFAULT_IMAGE) => {
  if (!value || typeof value !== "string") return fallback;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("//")) return `https:${value}`;
  if (value.startsWith("/")) return `${SITE_URL}${value}`;
  return `${SITE_URL}/${value.replace(/^\/+/, "")}`;
};

const getSeoMeta = (pathname) => {
  if (pathname === "/") {
    return {
      title: `${SITE_NAME} | Engineering Training & EPC Solutions`,
      description:
        "Advance your engineering career with practical training and EPC expertise in process, mechanical, petroleum, and plant operations.",
      image: DEFAULT_IMAGE,
    };
  }

  if (pathname === "/training") {
    return {
      title: `Engineering Courses | ${SITE_NAME}`,
      description:
        "Browse all Mags Engineering courses covering process design, piping, instrumentation, safety, and plant operations.",
      image: DEFAULT_IMAGE,
    };
  }

  if (pathname === "/training/professional-short-courses") {
    return {
      title: `Professional Short Courses | ${SITE_NAME}`,
      description:
        "Browse professional engineering short courses from Mags Engineering covering process, mechanical, piping, instrumentation, safety, and plant operations.",
      image: DEFAULT_IMAGE,
    };
  }

  if (pathname.startsWith("/training/executive-specialization-programs")) {
    return {
      title: `Executive Specialization Program | ${SITE_NAME}`,
      description:
        "Explore structured executive engineering specialization programs from Mags Engineering with discipline-focused course sequences.",
      image: DEFAULT_IMAGE,
    };
  }

  if (pathname === "/training-schedule") {
    return {
      title: `Training Schedule | ${SITE_NAME}`,
      description:
        "See upcoming Mags Engineering training dates, course timelines, and enrollment options for engineering professionals.",
      image: DEFAULT_IMAGE,
    };
  }

  if (pathname === "/events") {
    return {
      title: `Events and Webinars | ${SITE_NAME}`,
      description:
        "Browse upcoming Mags Engineering live events and webinars for engineering professionals.",
    };
  }

  if (pathname === "/projects") {
    return {
      title: `Engineering Services & Capabilities | ${SITE_NAME}`,
      description:
        "Explore Mags Engineering capabilities across oil and gas projects, process engineering studies, and technical consultancy.",
    };
  }

  if (pathname === "/services") {
    return {
      title: `Industries We Serve | ${SITE_NAME}`,
      description:
        "Discover how Mags Engineering supports process, mechanical, and energy teams with practical training and technical solutions.",
    };
  }

  if (pathname.startsWith("/services/")) {
    const serviceSlug = pathname.replace("/services/", "").split("/")[0];
    const service = experiences.find(
      (item) => getServiceSlug(item) === serviceSlug || item.id === Number(serviceSlug)
    );

    if (service?.role) {
      const serviceTitle = service.role.trim();
      return {
        title: `${serviceTitle} | ${SITE_NAME}`,
        description:
          summarizeDescription(
            service.desc,
            `${serviceTitle} services from ${SITE_NAME}.`
          ),
      };
    }

    return {
      title: `Service Details | ${SITE_NAME}`,
      description:
        "View detailed information about this Mags Engineering service offering and how it supports your project goals.",
    };
  }

  if (pathname.startsWith("/role/")) {
    return {
      title: `Training Category | ${SITE_NAME}`,
      description:
        "Explore specialized training tracks by role and engineering discipline at Mags Engineering Limited.",
    };
  }

  if (pathname.startsWith("/courses/")) {
    const slug = decodeURIComponent(pathname.replace("/courses/", "").split("/")[0]);
    const course = courses.find((item) => item.slug === slug);
    if (course) {
      return {
        title: `${course.title} | ${SITE_NAME}`,
        description: summarizeDescription(
          course.description,
          `${course.title} training by ${SITE_NAME}.`
        ),
        image: getAbsoluteUrl(course.image),
      };
    }
    return {
      title: `Course Not Found | ${SITE_NAME}`,
      description: "The requested course page could not be found. Explore other engineering courses from Mags Engineering.",
      image: DEFAULT_IMAGE,
    };
  }

  if (pathname === "/testimonials") {
    return {
      title: `Testimonials | ${SITE_NAME}`,
      description:
        "Read trainee feedback and success stories from engineering professionals who trained with Mags Engineering.",
    };
  }

  if (pathname === "/T&C") {
    return {
      title: `Terms & Conditions | ${SITE_NAME}`,
      description:
        "Review the terms and conditions for training enrollment, payments, access, and service use at Mags Engineering.",
    };
  }

  if (pathname === "/privacy") {
    return {
      title: `Privacy Policy | ${SITE_NAME}`,
      description:
        "Learn how Mags Engineering collects, uses, and protects your personal data across our website and training services.",
    };
  }

  if (pathname === "/refund") {
    return {
      title: `Refund Policy | ${SITE_NAME}`,
      description:
        "Understand Mags Engineering refund terms for course registration, cancellations, and payment-related requests.",
    };
  }

  if (pathname === "/forum") {
    return {
      title: `Engineering Forum | ${SITE_NAME}`,
      description:
        "Ask practical engineering questions and browse approved discussions on process, maintenance, installation, safety, and industrial operations.",
    };
  }

  if (pathname === "/forum/ask") {
    return {
      title: `Ask a Question | ${SITE_NAME}`,
      description:
        "Submit a technical engineering question to the Mags Engineering forum for admin review and community discussion.",
    };
  }

  if (pathname.startsWith("/forum/")) {
    return {
      title: `Forum Discussion | ${SITE_NAME}`,
      description:
        "Read engineering forum discussions and approved replies from the Mags Engineering community.",
    };
  }

  return {
    title: `${SITE_NAME} | Engineering Training`,
    description: DEFAULT_DESCRIPTION,
    image: DEFAULT_IMAGE,
  };
};

const SeoMeta = () => {
  const { pathname } = useLocation();
  const canonicalPath = getCanonicalPath(pathname);
  const meta = getSeoMeta(canonicalPath);
  const pageUrl = `${SITE_URL}${canonicalPath}`;
  const imageUrl = getAbsoluteUrl(meta.image);

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={imageUrl} />
      <link rel="canonical" href={pageUrl} />
    </Helmet>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
};

const PostHogPageTracker = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (posthog.__loaded) {
      posthog.capture("$pageview");
    }
  }, [pathname]);

  return null;
};


function App() {
  const [openModal, setOpenModal] = useState({ state: false, project: null });

  return (
    <ThemeProvider theme={lightTheme}>
      <Router>
        <ScrollToTop />
        <PostHogPageTracker />
        <SeoMeta />
        <Navbar />
        <Body>
          <Routes>
            <Route 
              path="/" 
              element={
                <>
                  <HeroSection />
                  <Wrapper>
                    <About />
                    <ServiceList />
                  </Wrapper>
                  <Wrapper>
                    <Testimonials />
                    <Contact />
                  </Wrapper>
                  
                </>
              } 
            />
            <Route 
              path="/training" 
              element={<Projects openModal={openModal} setOpenModal={setOpenModal} />} 
            />
            <Route
              path="/training/professional-short-courses"
              element={
                <Projects
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                  routeTrainingType="courses"
                />
              }
            />
            <Route
              path="/training/executive-specialization-programs"
              element={
                <Projects
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                  routeTrainingType="programs"
                />
              }
            />
            <Route
              path="/training/executive-specialization-programs/:programSlug"
              element={
                <Projects
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                  routeTrainingType="programs"
                />
              }
            />
            <Route path="/forum" element={<ForumPage />} />
            <Route path="/forum/ask" element={<AskQuestionPage />} />
            <Route path="/forum/:slug" element={<QuestionDetailPage />} />
            <Route 
              path="/training-schedule" 
              element={<TrainingSchedule openModal={openModal} setOpenModal={setOpenModal} />} 
            />
            <Route path="/events" element={<EventsWebinars />} />
            
            <Route 
              path="/projects" 
              element={<ProjectsOverview openModal={openModal} setOpenModal={setOpenModal} />} 
            />
            <Route path="/services" element={<ServiceList openModal={openModal} setOpenModal={setOpenModal}/>} />
            <Route path="/services/:slug" element={<ServiceDetail openModal={openModal} setOpenModal={setOpenModal}/>} />
            {/* <Route path="/" element={<ServicesPage />} /> */}
            <Route path="/courses/:slug" element={<CoursePage />} />
            <Route path="/T&C" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/testimonials" element={<Testimonials />} />

            <Route 
              path="/role/:id" 
              element={
                <RoleServiceDetail 
                  openModal={openModal} 
                  setOpenModal={setOpenModal} 
                />
              } 
            />
          </Routes>
          <Footer />
          
          
          {openModal.state && (
            <ProjectDetails openModal={openModal} setOpenModal={setOpenModal} />
          )}
        </Body>
      </Router>
    </ThemeProvider>
  );
}

export default App;
